/* ============================================================
   NovaX Web — Global State (Zustand)
   ============================================================ */

import { create } from 'zustand';
import type {
  ActiveView,
  ActivityEvent,
  BotInfo,
  CardState,
  ChatGroup,
  ChatMessage,
  ChatSession,
  ServerSession,
  ServerSessionMessage,
  Theme,
} from './types';

/* ---- team status types ---- */

export interface AgentMetadata {
  name: string;
  description?: string;
  model?: string;
  tools?: string;
}

export interface BotStatus {
  name: string;
  description?: string;
  specialties?: string[];
  icon?: string;
  platform: string;
  engine?: 'claude' | 'kimi' | 'codex';
  model?: string;
  workingDirectory: string;
  status: 'idle' | 'busy' | 'error';
  currentTask?: {
    chatId: string;
    startTime: number;
    durationMs: number;
  };
  stats: {
    totalTasks: number;
    completedTasks: number;
    failedTasks: number;
    totalCostUsd: number;
  };
  agents?: AgentMetadata[];
}

export interface TeamStatus {
  bots: BotStatus[];
  summary: {
    totalBots: number;
    busyBots: number;
    idleBots: number;
    totalCostUsd: number;
    totalTasks: number;
  };
}

/* ---- helpers ---- */

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function getSessionStorageKey(): string {
  const userId = localStorage.getItem('novax:userId') || '';
  return userId ? `novax:sessions:${userId}` : 'novax:sessions';
}

function persistSessions(sessions: Map<string, ChatSession>) {
  try {
    const obj: Record<string, ChatSession> = {};
    sessions.forEach((v, k) => {
      obj[k] = v;
    });
    localStorage.setItem(getSessionStorageKey(), JSON.stringify(obj));
  } catch {
    // storage full — silently ignore
  }
}

/* Debounced persistence — avoids writing to localStorage on every streaming
   token which causes severe UI lag on long conversations.  Structural changes
   (create / delete session) still use `persistSessions` for immediate writes. */
let _persistTimer: ReturnType<typeof setTimeout> | null = null;
let _lastSessions: Map<string, ChatSession> | null = null;
function debouncedPersistSessions(sessions: Map<string, ChatSession>) {
  _lastSessions = sessions;
  if (_persistTimer) clearTimeout(_persistTimer);
  _persistTimer = setTimeout(() => {
    _persistTimer = null;
    _lastSessions = null;
    persistSessions(sessions);
  }, 2000);
}

// Flush pending persist on page unload so streaming content isn't lost
window.addEventListener('beforeunload', () => {
  if (_persistTimer && _lastSessions) {
    clearTimeout(_persistTimer);
    persistSessions(_lastSessions);
  }
});

function loadSessions(): Map<string, ChatSession> {
  try {
    const key = getSessionStorageKey();
    const raw = localStorage.getItem(key);
    // Also try legacy unscoped key and migrate if found
    if (!raw) {
      const legacyRaw = localStorage.getItem('novax:sessions');
      if (!legacyRaw) return new Map();
      // Migrate: move legacy data to user-scoped key, then delete legacy
      const userId = localStorage.getItem('novax:userId') || '';
      if (userId) {
        localStorage.setItem(`novax:sessions:${userId}`, legacyRaw);
        localStorage.removeItem('novax:sessions');
        // fall through to parse
        const obj = JSON.parse(legacyRaw) as Record<string, ChatSession>;
        const map = new Map<string, ChatSession>();
        for (const [k, v] of Object.entries(obj)) map.set(k, v);
        return map;
      }
      // No userId yet — load legacy as-is
      const obj = JSON.parse(legacyRaw) as Record<string, ChatSession>;
      const map = new Map<string, ChatSession>();
      for (const [k, v] of Object.entries(obj)) map.set(k, v);
      return map;
    }
    const obj = JSON.parse(raw) as Record<string, ChatSession>;
    const map = new Map<string, ChatSession>();
    for (const [k, v] of Object.entries(obj)) {
      map.set(k, v);
    }
    return map;
  } catch {
    return new Map();
  }
}

/* ---- store interface ---- */

export interface AppStore {
  // Auth (cloud JWT)
  token: string | null;
  machineApiKey: string | null;
  userName: string;
  userEmail: string;
  userAvatar: string;
  login: (token: string, user?: { machineApiKey: string; name: string; email: string; avatarUrl?: string; id?: string }) => void;
  logout: () => void;

  // Daemon connection (local MetaBot via cloud relay)
  daemonConnected: boolean;
  setDaemonConnected: (c: boolean) => void;

  // Connection (MetaBot bots available)
  connected: boolean;
  setConnected: (c: boolean) => void;

  // Bots
  bots: BotInfo[];
  setBots: (bots: BotInfo[]) => void;
  activeBotName: string | null;
  setBot: (name: string) => void;

  // Sessions
  sessions: Map<string, ChatSession>;
  activeSessionId: string | null;
  createSession: (botName?: string) => string;
  deleteSession: (id: string) => void;
  setActiveSession: (id: string | null) => void;
  addMessage: (sessionId: string, msg: ChatMessage) => void;
  updateMessageState: (
    sessionId: string,
    messageId: string,
    state: CardState,
    botName?: string,
  ) => void;
  updateMessageText: (
    sessionId: string,
    messageId: string,
    text: string,
  ) => void;
  addMessageAttachment: (
    sessionId: string,
    messageId: string,
    attachment: import('./types').FileAttachment,
  ) => void;
  markRunningMessagesDisconnected: () => void;
  clearSessions: () => void;
  getOrCreateBotSession: (botName: string) => string;
  /** Rename a session locally. */
  renameSession: (id: string, title: string) => void;
  /** Merge server-side sessions into local store (session persistence). */
  mergeServerSessions: (serverSessions: ServerSession[]) => void;
  /** Load server message history into a local session. */
  loadServerHistory: (chatId: string, messages: ServerSessionMessage[]) => void;

  // Composer drafts injected by cross-module navigation (for example, when a
  // user opens the Agent responsible for a briefing article).
  composerDrafts: Record<string, string>;
  setComposerDraft: (sessionId: string, text: string) => void;
  clearComposerDraft: (sessionId: string) => void;

  // Unread DMs — set of session IDs with unread DM messages
  unreadDMs: Set<string>;
  markDmUnread: (sessionId: string) => void;
  clearDmUnread: (sessionId: string) => void;

  // Groups
  groups: ChatGroup[];
  setGroups: (groups: ChatGroup[]) => void;
  addGroup: (group: ChatGroup) => void;
  removeGroup: (groupId: string) => void;
  updateGroup: (group: ChatGroup) => void;
  createGroupSession: (group: ChatGroup) => string;

  // Navigation
  activeView: ActiveView;
  setView: (v: ActiveView) => void;

  // Theme
  theme: Theme;
  toggleTheme: () => void;

  // Font size
  fontSize: 'small' | 'normal' | 'large' | 'xl';
  setFontSize: (size: 'small' | 'normal' | 'large' | 'xl') => void;

  // Team
  teamStatus: TeamStatus | null;
  setTeamStatus: (status: TeamStatus) => void;
  teamViewMode: 'workspace' | 'office';
  setTeamViewMode: (mode: 'workspace' | 'office') => void;
  selectedAgentKey: string | null;
  setSelectedAgentKey: (key: string | null) => void;
  teamChatBotName: string | null;
  setTeamChatBotName: (name: string | null) => void;
  teamDetailTab: 'activity' | 'stats' | 'info';
  setTeamDetailTab: (tab: 'activity' | 'stats' | 'info') => void;

  // Activity feed
  activityEvents: ActivityEvent[];
  addActivityEvent: (event: ActivityEvent) => void;
  setActivityEvents: (events: ActivityEvent[]) => void;

  // Incoming voice call
  incomingVoiceCall: { sessionId: string; roomId: string; token: string; appId: string; userId: string; aiUserId: string; chatId: string; botName: string; prompt?: string } | null;
  setIncomingVoiceCall: (call: { sessionId: string; roomId: string; token: string; appId: string; userId: string; aiUserId: string; chatId: string; botName: string; prompt?: string } | null) => void;

  // Streaming ASR
  asrState: 'idle' | 'connecting' | 'recording' | 'active' | 'error';
  asrPartialText: string;
  setAsrState: (state: 'idle' | 'connecting' | 'recording' | 'active' | 'error') => void;
  setAsrPartialText: (text: string) => void;

  // Sidebar
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
}

export const useStore = create<AppStore>((set, get) => ({
  /* ---- Auth (cloud) ---- */
  token: localStorage.getItem('novax:token'),
  machineApiKey: localStorage.getItem('novax:machineApiKey'),
  userName: localStorage.getItem('novax:userName') || '',
  userEmail: localStorage.getItem('novax:userEmail') || '',
  userAvatar: localStorage.getItem('novax:userAvatar') || '',

  login(token: string, user?: { machineApiKey: string; name: string; email: string; avatarUrl?: string; id?: string }) {
    const resolvedUser = user ?? { machineApiKey: token, name: '', email: '' };
    // Detect user switch — clear previous user's in-memory sessions
    const prevUserId = localStorage.getItem('novax:userId') || '';
    const newUserId = resolvedUser.id || '';
    localStorage.setItem('novax:token', token);
    localStorage.setItem('novax:machineApiKey', resolvedUser.machineApiKey);
    localStorage.setItem('novax:userName', resolvedUser.name);
    localStorage.setItem('novax:displayName', resolvedUser.name);
    localStorage.setItem('novax:userEmail', resolvedUser.email);
    if (resolvedUser.id) localStorage.setItem('novax:userId', resolvedUser.id);
    if (resolvedUser.avatarUrl) localStorage.setItem('novax:userAvatar', resolvedUser.avatarUrl);
    // If switching users, reset sessions to the new user's stored sessions
    // (loadSessions now reads user-scoped key based on novax:userId)
    const switchedUser = newUserId !== prevUserId;
    const sessions = switchedUser ? loadSessions() : get().sessions;
    set({
      token, machineApiKey: resolvedUser.machineApiKey, userName: resolvedUser.name,
      userEmail: resolvedUser.email, userAvatar: resolvedUser.avatarUrl || '',
      ...(switchedUser ? { sessions, activeSessionId: null, groups: [], unreadDMs: new Set<string>() } : {}),
    });
  },

  logout() {
    // Remove user-scoped sessions (keep data for potential re-login, don't delete scoped key)
    localStorage.removeItem('novax:token');
    localStorage.removeItem('novax:machineApiKey');
    localStorage.removeItem('novax:userName');
    localStorage.removeItem('novax:userEmail');
    localStorage.removeItem('novax:userAvatar');
    localStorage.removeItem('novax:sessions'); // legacy unscoped key
    localStorage.removeItem('novax:displayName');
    set({ token: null, machineApiKey: null, userName: '', userEmail: '', userAvatar: '', connected: false, daemonConnected: false, bots: [], activeBotName: null, sessions: new Map(), activeSessionId: null, composerDrafts: {} });
  },

  /* ---- Daemon connection ---- */
  daemonConnected: false,
  setDaemonConnected(c: boolean) {
    set({ daemonConnected: c });
  },

  /* ---- Connection ---- */
  connected: false,
  setConnected(c: boolean) {
    set({ connected: c });
  },

  /* ---- Bots ---- */
  bots: [],
  setBots(bots: BotInfo[]) {
    const state = get();
    const updates: Partial<AppStore> = { bots };
    // Auto-select first bot if none selected
    if (!state.activeBotName && bots.length > 0) {
      updates.activeBotName = bots[0].name;
    }
    set(updates);
  },
  activeBotName: null,
  setBot(name: string) {
    set({ activeBotName: name });
  },

  /* ---- Sessions ---- */
  sessions: loadSessions(),
  activeSessionId: null,

  createSession(botName?: string) {
    const state = get();
    const bot = botName || state.activeBotName || 'default';
    const id = generateId();
    const session: ChatSession = {
      id,
      botName: bot,
      title: 'New Chat',
      messages: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    const sessions = new Map(state.sessions);
    sessions.set(id, session);
    persistSessions(sessions);
    set({ sessions, activeSessionId: id });
    return id;
  },

  deleteSession(id: string) {
    const sessions = new Map(get().sessions);
    sessions.delete(id);
    persistSessions(sessions);
    const updates: Partial<AppStore> = { sessions };
    if (get().activeSessionId === id) {
      // Select the most recent remaining session, or null
      const sorted = Array.from(sessions.values()).sort(
        (a, b) => b.updatedAt - a.updatedAt,
      );
      updates.activeSessionId = sorted.length > 0 ? sorted[0].id : null;
      updates.activeBotName = sorted.length > 0 ? sorted[0].botName : null;
    }
    set(updates);
  },

  setActiveSession(id: string | null) {
    if (id) {
      const sessions = new Map(get().sessions);
      const sess = sessions.get(id);
      if (sess) {
        sessions.set(id, { ...sess, lastReadAt: Date.now() });
        // Clear unread DM badge when opening the session
        const unread = new Set(get().unreadDMs);
        unread.delete(id);
        set({ activeSessionId: id, activeBotName: sess.botName, sessions, unreadDMs: unread });
        return;
      }
    }
    set({ activeSessionId: id });
  },

  addMessage(sessionId: string, msg: ChatMessage) {
    const sessions = new Map(get().sessions);
    const session = sessions.get(sessionId);
    if (!session) return;

    const updated: ChatSession = {
      ...session,
      messages: [...session.messages, msg],
      updatedAt: Date.now(),
      // Update title from first user message (but not for group chats)
      title:
        session.messages.length === 0 && msg.type === 'user' && !session.groupId
          ? msg.text.slice(0, 60) || msg.attachments?.[0]?.name.slice(0, 60) || 'New Chat'
          : session.title,
    };
    sessions.set(sessionId, updated);
    debouncedPersistSessions(sessions);
    set({ sessions });
  },

  updateMessageState(sessionId: string, messageId: string, state: CardState, botName?: string) {
    const sessions = new Map(get().sessions);
    const session = sessions.get(sessionId);
    if (!session) return;

    // Only update if we actually find the message — avoids phantom updatedAt changes
    // that make unrelated sessions "jump" in the sidebar
    let found = false;
    const messages = session.messages.map((m) => {
      if (m.id === messageId) {
        found = true;
        return { ...m, state, text: state.responseText || m.text, ...(botName ? { botName } : {}) };
      }
      return m;
    });
    if (!found) return;
    sessions.set(sessionId, { ...session, messages, updatedAt: Date.now() });
    debouncedPersistSessions(sessions);
    set({ sessions });
  },

  updateMessageText(sessionId: string, messageId: string, text: string) {
    const sessions = new Map(get().sessions);
    const session = sessions.get(sessionId);
    if (!session) return;

    const messages = session.messages.map((m) =>
      m.id === messageId ? { ...m, text } : m,
    );
    sessions.set(sessionId, { ...session, messages, updatedAt: Date.now() });
    debouncedPersistSessions(sessions);
    set({ sessions });
  },

  addMessageAttachment(sessionId: string, messageId: string, attachment: import('./types').FileAttachment) {
    const sessions = new Map(get().sessions);
    const session = sessions.get(sessionId);
    if (!session) return;

    const messages = session.messages.map((m) => {
      if (m.id === messageId) {
        return { ...m, attachments: [...(m.attachments || []), attachment] };
      }
      return m;
    });
    sessions.set(sessionId, { ...session, messages, updatedAt: Date.now() });
    debouncedPersistSessions(sessions);
    set({ sessions });
  },

  markRunningMessagesDisconnected() {
    const sessions = new Map(get().sessions);
    let anyChanged = false;
    for (const [id, session] of sessions) {
      let changed = false;
      const messages = session.messages.map((m) => {
        if (m.type === 'assistant' && m.state && (m.state.status === 'thinking' || m.state.status === 'running')) {
          changed = true;
          return { ...m, state: { ...m.state, status: 'error' as const, errorMessage: 'Connection lost' } };
        }
        return m;
      });
      if (changed) {
        sessions.set(id, { ...session, messages });
        anyChanged = true;
      }
    }
    if (anyChanged) {
      persistSessions(sessions);
      set({ sessions });
    }
  },

  clearSessions() {
    localStorage.removeItem('novax:sessions');
    set({ sessions: new Map(), activeSessionId: null });
  },

  getOrCreateBotSession(botName: string) {
    const state = get();
    // Find the most recently updated session for this bot
    let bestSession: ChatSession | undefined;
    for (const [, session] of state.sessions) {
      if (session.botName === botName && (!bestSession || session.updatedAt > bestSession.updatedAt)) {
        bestSession = session;
      }
    }
    if (bestSession) {
      set({ activeSessionId: bestSession.id, activeBotName: botName });
      return bestSession.id;
    }
    // Create new session for this bot
    const id = generateId();
    const session: ChatSession = {
      id,
      botName,
      title: 'New Chat',
      messages: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    const sessions = new Map(state.sessions);
    sessions.set(id, session);
    persistSessions(sessions);
    set({ sessions, activeSessionId: id, activeBotName: botName });
    return id;
  },

  renameSession(id: string, title: string) {
    const sessions = new Map(get().sessions);
    const session = sessions.get(id);
    if (!session) return;
    sessions.set(id, { ...session, title });
    persistSessions(sessions);
    set({ sessions });
  },

  mergeServerSessions(serverSessions: ServerSession[]) {
    const sessions = new Map(get().sessions);
    let changed = false;
    for (const ss of serverSessions) {
      // Skip group chat sessions — they are managed separately via groups_list
      // Group sessions have chatId like "group-{groupId}-{botName}"
      if (ss.chatId.startsWith('group-') || ss.chatId.startsWith('grouptalk-')) continue;
      // Server sessions use chatId as key — same as local session ID
      if (!sessions.has(ss.chatId)) {
        // Session exists on server but not locally — restore it
        sessions.set(ss.chatId, {
          id: ss.chatId,
          botName: ss.botName,
          title: ss.title || 'Restored Chat',
          messages: [],
          createdAt: ss.createdAt,
          updatedAt: ss.updatedAt,
        });
        changed = true;
      }
    }
    if (changed) {
      persistSessions(sessions);
      set({ sessions });
    }
  },

  loadServerHistory(chatId: string, messages: ServerSessionMessage[]) {
    const sessions = new Map(get().sessions);
    const session = sessions.get(chatId);
    if (!session) return;
    // Only load if local session has fewer messages (avoid overwriting in-progress work)
    if (session.messages.length >= messages.length) return;

    const chatMessages: ChatMessage[] = messages.map((m, i) => ({
      id: `srv-${chatId}-${i}`,
      type: m.role === 'user' ? 'user' as const : 'assistant' as const,
      text: m.text,
      timestamp: m.timestamp,
      state: m.role === 'assistant' ? {
        status: 'complete' as const,
        userPrompt: '',
        responseText: m.text,
        toolCalls: [],
        costUsd: m.costUsd,
        durationMs: m.durationMs,
        quickReplies: m.quickReplies,
      } : undefined,
    }));
    sessions.set(chatId, { ...session, messages: chatMessages });
    debouncedPersistSessions(sessions);
    set({ sessions });
  },

  /* ---- Unread DMs ---- */
  unreadDMs: new Set<string>(),
  markDmUnread(sessionId: string) {
    const s = new Set(get().unreadDMs);
    s.add(sessionId);
    set({ unreadDMs: s });
  },
  clearDmUnread(sessionId: string) {
    const s = new Set(get().unreadDMs);
    if (s.delete(sessionId)) set({ unreadDMs: s });
  },

  /* ---- Groups ---- */
  groups: [],
  setGroups(groups: ChatGroup[]) {
    set({ groups });
  },
  addGroup(group: ChatGroup) {
    set({ groups: [...get().groups, group] });
  },
  removeGroup(groupId: string) {
    // Also remove the group session
    const sessions = new Map(get().sessions);
    for (const [id, sess] of sessions) {
      if (sess.groupId === groupId) {
        sessions.delete(id);
      }
    }
    persistSessions(sessions);
    const updates: Partial<AppStore> = { groups: get().groups.filter((g) => g.id !== groupId), sessions };
    // If active session was in this group, deselect
    const activeId = get().activeSessionId;
    if (activeId && !sessions.has(activeId)) {
      updates.activeSessionId = null;
    }
    set(updates);
  },
  updateGroup(group: ChatGroup) {
    const groups = get().groups.map((g) => g.id === group.id ? group : g);
    // Also update the session title if it matches
    const sessions = new Map(get().sessions);
    for (const [id, sess] of sessions) {
      if (sess.groupId === group.id && sess.title !== group.name) {
        sessions.set(id, { ...sess, title: group.name, botName: group.name });
      }
    }
    persistSessions(sessions);
    set({ groups, sessions });
  },
  createGroupSession(group: ChatGroup) {
    const state = get();
    const id = generateId();
    const session: ChatSession = {
      id,
      botName: group.name,  // display name in sidebar
      title: group.name,
      messages: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
      groupId: group.id,
    };
    const sessions = new Map(state.sessions);
    sessions.set(id, session);
    persistSessions(sessions);
    set({ sessions, activeSessionId: id, activeView: 'chat' });
    return id;
  },

  composerDrafts: {},
  setComposerDraft(sessionId: string, text: string) {
    set({ composerDrafts: { ...get().composerDrafts, [sessionId]: text } });
  },
  clearComposerDraft(sessionId: string) {
    const drafts = { ...get().composerDrafts };
    delete drafts[sessionId];
    set({ composerDrafts: drafts });
  },

  /* ---- Navigation ---- */
  activeView: (['chat','memory','voice','settings','team','skillMarket','meetings','briefings','ontology'] as ActiveView[]).find(v=>v===new URLSearchParams(location.search).get('view')) || 'chat',
  setView(v: ActiveView) {
    set({ activeView: v });
  },

  /* ---- Theme ---- */
  theme: (localStorage.getItem('novax:theme') as Theme) || 'dark',
  toggleTheme() {
    const next = get().theme === 'dark' ? 'light' : 'dark';
    localStorage.setItem('novax:theme', next);
    document.documentElement.setAttribute('data-theme', next);
    set({ theme: next });
  },

  /* ---- Font size ---- */
  fontSize: (localStorage.getItem('novax:fontsize') as 'small' | 'normal' | 'large' | 'xl') || 'normal',
  setFontSize(size: 'small' | 'normal' | 'large' | 'xl') {
    const scales: Record<string, string> = { small: '0.9', normal: '1', large: '1.1', xl: '1.25' };
    localStorage.setItem('novax:fontsize', size);
    document.documentElement.style.setProperty('--font-scale', scales[size] || '1');
    set({ fontSize: size });
  },

  /* ---- Team ---- */
  teamStatus: null,
  setTeamStatus(status: TeamStatus) {
    set({ teamStatus: status });
  },
  teamViewMode: (localStorage.getItem('novax:teamViewMode') as 'workspace' | 'office') || 'workspace',
  setTeamViewMode(mode: 'workspace' | 'office') {
    localStorage.setItem('novax:teamViewMode', mode);
    set({ teamViewMode: mode });
  },
  selectedAgentKey: null,
  setSelectedAgentKey(key: string | null) {
    set({ selectedAgentKey: key });
  },
  teamChatBotName: null,
  setTeamChatBotName(name: string | null) {
    set({ teamChatBotName: name });
  },
  teamDetailTab: 'activity',
  setTeamDetailTab(tab: 'activity' | 'stats' | 'info') {
    set({ teamDetailTab: tab });
  },

  /* ---- Activity feed ---- */
  activityEvents: [],
  addActivityEvent(event: ActivityEvent) {
    const events = [event, ...get().activityEvents].slice(0, 100);
    set({ activityEvents: events });
  },
  setActivityEvents(events: ActivityEvent[]) {
    set({ activityEvents: events });
  },

  /* ---- Incoming voice call ---- */
  incomingVoiceCall: null,
  setIncomingVoiceCall(call) {
    set({ incomingVoiceCall: call });
  },

  /* ---- Streaming ASR ---- */
  asrState: 'idle' as 'idle' | 'connecting' | 'recording' | 'active' | 'error',
  asrPartialText: '',
  setAsrState(state: 'idle' | 'connecting' | 'recording' | 'active' | 'error') { set({ asrState: state }); },
  setAsrPartialText(text: string) { set({ asrPartialText: text }); },

  /* ---- Sidebar ---- */
  sidebarOpen: window.innerWidth > 768,
  toggleSidebar() {
    set({ sidebarOpen: !get().sidebarOpen });
  },
  setSidebarOpen(open: boolean) {
    set({ sidebarOpen: open });
  },
}));
